import React, { useState, useEffect, useRef } from 'react';
import {
    Mail, Phone, MapPin, Calendar, Camera, Edit2, Save, X, AlertCircle,
    ShieldCheck, ShieldOff, Lock, Briefcase, CreditCard, Heart, IdCard,
    Users, Clock, Loader, Monitor, Eye, EyeOff, Building2, MessageSquare,
    Bookmark, FileText, SlidersHorizontal, Bell
} from 'lucide-react';
import { useAuth, useNotification } from '../../../App';
import profileService from '../../../services/profileService';
import dashboardService from '../../../services/dashboardService';
import { useSettings } from '../../../hooks/useDashboard';

const emptyTenantProfile = {
    monthlyIncomeRange: '',
    householdSize: '',
    pets: false,
    preferredMoveInDate: '',
    emergencyContact: { name: '', phone: '', relationship: '' },
    previousLandlordReference: { name: '', phone: '', propertyAddress: '' }
};

const Card = ({ title, action, children }) => (
    <div className="bg-white rounded-xl shadow-sm border border-soil-100 p-6">
        <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-base font-semibold text-soil-900">{title}</h2>
            {action}
        </div>
        {children}
    </div>
);

const Field = ({ label, children }) => (
    <div>
        <label className="block text-xs font-medium text-soil-600 mb-1">{label}</label>
        {children}
    </div>
);

const inputClass = "block w-full px-3 py-2 border border-soil-100 rounded-lg text-sm focus:ring-2 focus:ring-highland-500 focus:border-transparent transition-colors";

const ReadRow = ({ icon: Icon, label, value }) => (
    <div className="flex items-start space-x-3 py-2.5">
        <div className="flex-shrink-0 p-2 bg-soil-100 rounded-lg">
            <Icon className="h-4 w-4 text-soil-600" />
        </div>
        <div>
            <p className="text-xs text-soil-400">{label}</p>
            <p className="text-sm font-medium text-soil-900">{value || 'Not provided'}</p>
        </div>
    </div>
);

const UserProfile = () => {
    const { user, establishSession } = useAuth();
    const { addNotification } = useNotification();

    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const [stats, setStats] = useState({ saved: 0, applications: 0, messages: 0 });

    const fileInputRef = useRef(null);
    const [avatarUploading, setAvatarUploading] = useState(false);

    const [editingIdentity, setEditingIdentity] = useState(false);
    const [identityForm, setIdentityForm] = useState({});
    const [savingIdentity, setSavingIdentity] = useState(false);

    const [editingRental, setEditingRental] = useState(false);
    const [rentalForm, setRentalForm] = useState({});
    const [savingRental, setSavingRental] = useState(false);

    const [requestingCode, setRequestingCode] = useState(false);
    const [phoneCodeSent, setPhoneCodeSent] = useState(false);
    const [phoneCode, setPhoneCode] = useState('');
    const [confirmingCode, setConfirmingCode] = useState(false);

    const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [showPasswords, setShowPasswords] = useState(false);
    const [changingPassword, setChangingPassword] = useState(false);

    const [sessions, setSessions] = useState([]);
    const [loginHistory, setLoginHistory] = useState([]);
    const [sessionsLoading, setSessionsLoading] = useState(true);
    const [revokingId, setRevokingId] = useState(null);

    const { settings, loading: settingsLoading, updating: savingSettings, updateSettings } = useSettings();
    const [settingsForm, setSettingsForm] = useState({
        budgetMin: 0,
        budgetMax: 100000,
        theme: 'light',
        emailNotifications: true,
        smsNotifications: false,
        pushNotifications: true
    });

    useEffect(() => {
        if (settings) {
            setSettingsForm({
                budgetMin: settings.searchPreferences?.budgetRange?.min || 0,
                budgetMax: settings.searchPreferences?.budgetRange?.max || 100000,
                theme: settings.appPreferences?.theme || 'light',
                emailNotifications: settings.notificationPreferences?.email !== false,
                smsNotifications: settings.notificationPreferences?.sms === true,
                pushNotifications: settings.notificationPreferences?.push !== false
            });
        }
    }, [settings]);

    const handleSaveSettings = async () => {
        const result = await updateSettings({
            budgetRange: { min: settingsForm.budgetMin, max: settingsForm.budgetMax },
            theme: settingsForm.theme,
            notificationPreferences: {
                email: settingsForm.emailNotifications,
                sms: settingsForm.smsNotifications,
                push: settingsForm.pushNotifications
            }
        });

        if (result.success) {
            addNotification('Preferences saved', 'success');
        } else {
            addNotification(result.error || 'Failed to save preferences', 'error');
        }
    };

    const applyProfile = (data) => {
        setProfile(data);
        setIdentityForm({
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            phone: data.phoneNumber || '',
            location: data.location || '',
            bio: data.bio || '',
            dateOfBirth: data.dateOfBirth || ''
        });
        setRentalForm({
            occupation: data.occupation || '',
            employer: data.employer || '',
            nationalId: data.nationalId || '',
            kraPin: data.kraPin || '',
            tenantProfile: {
                ...emptyTenantProfile,
                ...(data.tenantProfile || {}),
                emergencyContact: { ...emptyTenantProfile.emergencyContact, ...(data.tenantProfile?.emergencyContact || {}) },
                previousLandlordReference: { ...emptyTenantProfile.previousLandlordReference, ...(data.tenantProfile?.previousLandlordReference || {}) }
            }
        });
    };

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                setLoadError(null);
                const data = await profileService.getProfile();
                applyProfile(data);
            } catch (err) {
                setLoadError(err.message || 'Failed to load profile');
            } finally {
                setLoading(false);
            }
        })();

        (async () => {
            try {
                const [saved, applications, messages] = await Promise.all([
                    dashboardService.getSavedProperties(),
                    dashboardService.getApplications(),
                    dashboardService.getMessages()
                ]);
                setStats({ saved: saved.length, applications: applications.length, messages: messages.length });
            } catch {
                // Stats are a nice-to-have - leave at 0 rather than blocking the page
            }
        })();

        (async () => {
            try {
                setSessionsLoading(true);
                const [sessionList, history] = await Promise.all([
                    profileService.getSessions(),
                    profileService.getLoginHistory()
                ]);
                setSessions(sessionList);
                setLoginHistory(history);
            } catch {
                // Leave empty on failure
            } finally {
                setSessionsLoading(false);
            }
        })();
    }, []);

    const fullName = profile?.firstName && profile?.lastName ? `${profile.firstName} ${profile.lastName}` : user?.email || 'Your Profile';
    const initials = (profile?.firstName?.[0] || fullName[0] || 'U').toUpperCase();

    const handleAvatarClick = () => fileInputRef.current?.click();

    const handleAvatarChange = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;

        setAvatarUploading(true);
        try {
            const result = await profileService.uploadAvatar(file);
            setProfile((prev) => ({ ...prev, profileImage: result.profileImage }));
            await establishSession({ ...user, profileImage: result.profileImage });
            addNotification('Profile photo updated', 'success');
        } catch (err) {
            addNotification(err.message || 'Failed to upload photo', 'error');
        } finally {
            setAvatarUploading(false);
        }
    };

    const handleSaveIdentity = async () => {
        setSavingIdentity(true);
        try {
            const updated = await profileService.updateProfile({
                firstName: identityForm.firstName,
                lastName: identityForm.lastName,
                phone: identityForm.phone,
                location: identityForm.location,
                bio: identityForm.bio,
                dateOfBirth: identityForm.dateOfBirth || null
            });
            applyProfile(updated);
            await establishSession(updated);
            setEditingIdentity(false);
            addNotification('Profile updated', 'success');
        } catch (err) {
            addNotification(err.message || 'Failed to update profile', 'error');
        } finally {
            setSavingIdentity(false);
        }
    };

    const handleSaveRental = async () => {
        setSavingRental(true);
        try {
            const updated = await profileService.updateProfile({
                occupation: rentalForm.occupation,
                employer: rentalForm.employer,
                nationalId: rentalForm.nationalId,
                kraPin: rentalForm.kraPin,
                tenantProfile: rentalForm.tenantProfile
            });
            applyProfile(updated);
            setEditingRental(false);
            addNotification('Rental profile updated', 'success');
        } catch (err) {
            addNotification(err.message || 'Failed to update rental profile', 'error');
        } finally {
            setSavingRental(false);
        }
    };

    const handleRequestCode = async () => {
        setRequestingCode(true);
        try {
            const result = await profileService.requestPhoneVerification();
            setPhoneCodeSent(true);
            addNotification(result.message || 'Verification code sent', 'info');
        } catch (err) {
            addNotification(err.message || 'Failed to send verification code', 'error');
        } finally {
            setRequestingCode(false);
        }
    };

    const handleConfirmCode = async () => {
        if (!phoneCode.trim()) return;
        setConfirmingCode(true);
        try {
            const updated = await profileService.confirmPhoneVerification(phoneCode.trim());
            applyProfile(updated);
            await establishSession(updated);
            setPhoneCodeSent(false);
            setPhoneCode('');
            addNotification('Phone number verified', 'success');
        } catch (err) {
            addNotification(err.message || 'Invalid verification code', 'error');
        } finally {
            setConfirmingCode(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            addNotification('New passwords do not match', 'error');
            return;
        }
        setChangingPassword(true);
        try {
            await profileService.changePassword(passwordForm);
            setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
            addNotification('Password changed successfully', 'success');
        } catch (err) {
            addNotification(err.message || 'Failed to change password', 'error');
        } finally {
            setChangingPassword(false);
        }
    };

    const handleRevokeSession = async (sessionId) => {
        setRevokingId(sessionId);
        try {
            await profileService.revokeSession(sessionId);
            setSessions((prev) => prev.filter((s) => s.sessionId !== sessionId));
            addNotification('Session revoked', 'success');
        } catch (err) {
            addNotification(err.message || 'Failed to revoke session', 'error');
        } finally {
            setRevokingId(null);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-karibu-100">
                <div className="text-center">
                    <Loader className="h-10 w-10 text-highland-500 animate-spin mx-auto mb-4" />
                    <p className="text-soil-600">Loading your profile...</p>
                </div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-karibu-100">
                <div className="text-center bg-white p-8 rounded-lg shadow-md max-w-md">
                    <AlertCircle className="h-12 w-12 text-murram-500 mx-auto mb-4" />
                    <h2 className="font-display text-xl font-semibold text-soil-900 mb-2">Couldn't load your profile</h2>
                    <p className="text-soil-600 mb-4">{loadError}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-4 py-2 bg-highland-500 text-white rounded-lg hover:bg-highland-600"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    const completionPct = profile?.profileCompletionPercentage ?? 0;

    return (
        <div className="min-h-screen bg-karibu-100 pt-20 pb-8">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Profile Header */}
                <div className="bg-white rounded-xl shadow-sm border border-soil-100 p-8 mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-6">
                        <div className="relative flex-shrink-0">
                            <div className="h-20 w-20 rounded-full bg-highland-500 flex items-center justify-center text-white text-2xl font-bold font-display overflow-hidden">
                                {profile?.profileImage ? (
                                    <img src={profile.profileImage} alt={fullName} className="w-full h-full object-cover" />
                                ) : (
                                    initials
                                )}
                            </div>
                            <button
                                onClick={handleAvatarClick}
                                disabled={avatarUploading}
                                aria-label="Change profile photo"
                                className="absolute -bottom-1 -right-1 h-8 w-8 bg-soil-900 text-white rounded-full flex items-center justify-center hover:bg-soil-700 transition-colors disabled:opacity-50"
                            >
                                {avatarUploading ? <Loader className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
                            </button>
                            <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/gif,image/webp" className="hidden" onChange={handleAvatarChange} />
                        </div>

                        <div className="flex-1">
                            <h1 className="font-display text-2xl font-bold text-soil-900">{fullName}</h1>
                            <p className="text-sm text-soil-600 mt-0.5">{profile?.email}</p>
                            <div className="flex items-center text-xs text-soil-400 mt-1">
                                <Calendar className="h-3.5 w-3.5 mr-1" />
                                Member since {profile?.dateCreated ? new Date(profile.dateCreated).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'recently'}
                            </div>

                            <div className="mt-3 max-w-xs">
                                <div className="flex items-center justify-between text-xs text-soil-600 mb-1">
                                    <span>Profile completion</span>
                                    <span className="font-medium">{completionPct}%</span>
                                </div>
                                <div className="w-full bg-soil-100 rounded-full h-1.5">
                                    <div className="h-1.5 rounded-full bg-highland-500 transition-all duration-300" style={{ width: `${completionPct}%` }} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Left column */}
                    <div className="space-y-6">
                        {/* Identity & Contact */}
                        <Card
                            title="Contact Information"
                            action={
                                editingIdentity ? (
                                    <div className="flex items-center space-x-2">
                                        <button onClick={() => { setEditingIdentity(false); applyProfile(profile); }} className="text-soil-400 hover:text-soil-600">
                                            <X className="h-4 w-4" />
                                        </button>
                                        <button onClick={handleSaveIdentity} disabled={savingIdentity} className="flex items-center text-highland-500 hover:text-highland-600 text-sm font-medium disabled:opacity-50">
                                            {savingIdentity ? <Loader className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                                            Save
                                        </button>
                                    </div>
                                ) : (
                                    <button onClick={() => setEditingIdentity(true)} className="text-soil-400 hover:text-highland-500">
                                        <Edit2 className="h-4 w-4" />
                                    </button>
                                )
                            }
                        >
                            {editingIdentity ? (
                                <div className="space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                        <Field label="First Name">
                                            <input className={inputClass} value={identityForm.firstName} onChange={(e) => setIdentityForm((f) => ({ ...f, firstName: e.target.value }))} />
                                        </Field>
                                        <Field label="Last Name">
                                            <input className={inputClass} value={identityForm.lastName} onChange={(e) => setIdentityForm((f) => ({ ...f, lastName: e.target.value }))} />
                                        </Field>
                                    </div>
                                    <Field label="Phone Number">
                                        <input className={inputClass} value={identityForm.phone} onChange={(e) => setIdentityForm((f) => ({ ...f, phone: e.target.value }))} placeholder="+254 700 123 456" />
                                    </Field>
                                    <Field label="Location">
                                        <input className={inputClass} value={identityForm.location} onChange={(e) => setIdentityForm((f) => ({ ...f, location: e.target.value }))} placeholder="Nairobi" />
                                    </Field>
                                    <Field label="Date of Birth">
                                        <input type="date" className={inputClass} value={identityForm.dateOfBirth} onChange={(e) => setIdentityForm((f) => ({ ...f, dateOfBirth: e.target.value }))} />
                                    </Field>
                                    <Field label="Bio">
                                        <textarea className={inputClass} rows={3} value={identityForm.bio} onChange={(e) => setIdentityForm((f) => ({ ...f, bio: e.target.value }))} placeholder="A short line about yourself" />
                                    </Field>
                                </div>
                            ) : (
                                <div className="divide-y divide-soil-100">
                                    <ReadRow icon={Mail} label="Email" value={profile?.email} />
                                    <ReadRow icon={Phone} label="Phone" value={profile?.phoneNumber} />
                                    <ReadRow icon={MapPin} label="Location" value={profile?.location} />
                                    <ReadRow icon={Calendar} label="Date of Birth" value={profile?.dateOfBirth} />
                                    {profile?.bio && <ReadRow icon={FileText} label="Bio" value={profile.bio} />}
                                </div>
                            )}
                        </Card>

                        {/* Verification */}
                        <Card title="Verification">
                            <div className="space-y-3">
                                <div className="flex items-center justify-between py-1">
                                    <div className="flex items-center space-x-2">
                                        <ShieldCheck className={`h-4 w-4 ${profile?.emailVerified ? 'text-highland-500' : 'text-soil-400'}`} />
                                        <span className="text-sm text-soil-900">Email</span>
                                    </div>
                                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${profile?.emailVerified ? 'bg-highland-50 text-highland-600' : 'bg-soil-100 text-soil-500'}`}>
                                        {profile?.emailVerified ? 'Verified' : 'Not verified'}
                                    </span>
                                </div>

                                <div className="flex items-center justify-between py-1">
                                    <div className="flex items-center space-x-2">
                                        {profile?.phoneVerified ? <ShieldCheck className="h-4 w-4 text-highland-500" /> : <ShieldOff className="h-4 w-4 text-soil-400" />}
                                        <span className="text-sm text-soil-900">Phone</span>
                                    </div>
                                    {profile?.phoneVerified ? (
                                        <span className="text-xs font-medium px-2 py-1 rounded-full bg-highland-50 text-highland-600">Verified</span>
                                    ) : (
                                        <button onClick={handleRequestCode} disabled={requestingCode || !profile?.phoneNumber} className="text-xs font-medium text-highland-500 hover:text-highland-600 disabled:opacity-50">
                                            {requestingCode ? 'Sending...' : 'Verify now'}
                                        </button>
                                    )}
                                </div>

                                {!profile?.phoneNumber && !profile?.phoneVerified && (
                                    <p className="text-xs text-soil-400">Add a phone number above before verifying it.</p>
                                )}

                                {phoneCodeSent && !profile?.phoneVerified && (
                                    <div className="flex items-center space-x-2 pt-2">
                                        <input
                                            className={`${inputClass} flex-1`}
                                            placeholder="6-digit code"
                                            maxLength={6}
                                            value={phoneCode}
                                            onChange={(e) => setPhoneCode(e.target.value)}
                                        />
                                        <button onClick={handleConfirmCode} disabled={confirmingCode} className="px-3 py-2 bg-highland-500 text-white rounded-lg text-sm font-medium hover:bg-highland-600 disabled:opacity-50">
                                            {confirmingCode ? <Loader className="h-4 w-4 animate-spin" /> : 'Confirm'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </Card>

                        {/* Change Password */}
                        <Card title="Change Password">
                            <form onSubmit={handleChangePassword} className="space-y-3">
                                <Field label="Current Password">
                                    <input type={showPasswords ? 'text' : 'password'} className={inputClass} value={passwordForm.currentPassword} onChange={(e) => setPasswordForm((f) => ({ ...f, currentPassword: e.target.value }))} required />
                                </Field>
                                <Field label="New Password">
                                    <input type={showPasswords ? 'text' : 'password'} className={inputClass} value={passwordForm.newPassword} onChange={(e) => setPasswordForm((f) => ({ ...f, newPassword: e.target.value }))} required />
                                </Field>
                                <Field label="Confirm New Password">
                                    <input type={showPasswords ? 'text' : 'password'} className={inputClass} value={passwordForm.confirmPassword} onChange={(e) => setPasswordForm((f) => ({ ...f, confirmPassword: e.target.value }))} required />
                                </Field>
                                <div className="flex items-center justify-between pt-1">
                                    <button type="button" onClick={() => setShowPasswords((s) => !s)} className="flex items-center text-xs text-soil-500 hover:text-soil-700">
                                        {showPasswords ? <EyeOff className="h-3.5 w-3.5 mr-1" /> : <Eye className="h-3.5 w-3.5 mr-1" />}
                                        {showPasswords ? 'Hide' : 'Show'} passwords
                                    </button>
                                    <button type="submit" disabled={changingPassword} className="flex items-center px-4 py-2 bg-highland-500 text-white rounded-lg text-sm font-medium hover:bg-highland-600 disabled:opacity-50">
                                        {changingPassword ? <Loader className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
                                        Update Password
                                    </button>
                                </div>
                            </form>
                        </Card>
                    </div>

                    {/* Right column */}
                    <div className="space-y-6">
                        {/* Activity stats */}
                        <div className="grid grid-cols-3 gap-4">
                            <div className="bg-white rounded-lg shadow-sm border border-soil-100 p-4 text-center">
                                <Bookmark className="h-5 w-5 text-highland-500 mx-auto mb-1.5" />
                                <p className="font-mono text-xl font-bold text-soil-900">{stats.saved}</p>
                                <p className="text-xs text-soil-600">Saved</p>
                            </div>
                            <div className="bg-white rounded-lg shadow-sm border border-soil-100 p-4 text-center">
                                <FileText className="h-5 w-5 text-jacaranda-500 mx-auto mb-1.5" />
                                <p className="font-mono text-xl font-bold text-soil-900">{stats.applications}</p>
                                <p className="text-xs text-soil-600">Applications</p>
                            </div>
                            <div className="bg-white rounded-lg shadow-sm border border-soil-100 p-4 text-center">
                                <MessageSquare className="h-5 w-5 text-dawn-600 mx-auto mb-1.5" />
                                <p className="font-mono text-xl font-bold text-soil-900">{stats.messages}</p>
                                <p className="text-xs text-soil-600">Messages</p>
                            </div>
                        </div>

                        {/* Rental Readiness */}
                        <Card
                            title="Rental Readiness"
                            action={
                                editingRental ? (
                                    <div className="flex items-center space-x-2">
                                        <button onClick={() => { setEditingRental(false); applyProfile(profile); }} className="text-soil-400 hover:text-soil-600">
                                            <X className="h-4 w-4" />
                                        </button>
                                        <button onClick={handleSaveRental} disabled={savingRental} className="flex items-center text-highland-500 hover:text-highland-600 text-sm font-medium disabled:opacity-50">
                                            {savingRental ? <Loader className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                                            Save
                                        </button>
                                    </div>
                                ) : (
                                    <button onClick={() => setEditingRental(true)} className="text-soil-400 hover:text-highland-500">
                                        <Edit2 className="h-4 w-4" />
                                    </button>
                                )
                            }
                        >
                            <p className="text-xs text-soil-400 mb-3">This pre-fills your rental applications so you don't have to retype it every time.</p>
                            {editingRental ? (
                                <div className="space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                        <Field label="Occupation">
                                            <input className={inputClass} value={rentalForm.occupation} onChange={(e) => setRentalForm((f) => ({ ...f, occupation: e.target.value }))} />
                                        </Field>
                                        <Field label="Employer">
                                            <input className={inputClass} value={rentalForm.employer} onChange={(e) => setRentalForm((f) => ({ ...f, employer: e.target.value }))} />
                                        </Field>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <Field label="National ID">
                                            <input className={inputClass} value={rentalForm.nationalId} onChange={(e) => setRentalForm((f) => ({ ...f, nationalId: e.target.value }))} />
                                        </Field>
                                        <Field label="KRA PIN">
                                            <input className={inputClass} value={rentalForm.kraPin} onChange={(e) => setRentalForm((f) => ({ ...f, kraPin: e.target.value }))} />
                                        </Field>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <Field label="Monthly Income Range">
                                            <input className={inputClass} placeholder="e.g. 100000-150000" value={rentalForm.tenantProfile.monthlyIncomeRange} onChange={(e) => setRentalForm((f) => ({ ...f, tenantProfile: { ...f.tenantProfile, monthlyIncomeRange: e.target.value } }))} />
                                        </Field>
                                        <Field label="Household Size">
                                            <input type="number" min="1" className={inputClass} value={rentalForm.tenantProfile.householdSize} onChange={(e) => setRentalForm((f) => ({ ...f, tenantProfile: { ...f.tenantProfile, householdSize: e.target.value } }))} />
                                        </Field>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 items-end">
                                        <Field label="Preferred Move-in Date">
                                            <input type="date" className={inputClass} value={rentalForm.tenantProfile.preferredMoveInDate} onChange={(e) => setRentalForm((f) => ({ ...f, tenantProfile: { ...f.tenantProfile, preferredMoveInDate: e.target.value } }))} />
                                        </Field>
                                        <label className="flex items-center space-x-2 pb-2">
                                            <input type="checkbox" className="h-4 w-4 text-highland-500 border-soil-100 rounded" checked={!!rentalForm.tenantProfile.pets} onChange={(e) => setRentalForm((f) => ({ ...f, tenantProfile: { ...f.tenantProfile, pets: e.target.checked } }))} />
                                            <span className="text-sm text-soil-700">I have pets</span>
                                        </label>
                                    </div>

                                    <p className="text-xs font-medium text-soil-600 pt-1">Emergency Contact</p>
                                    <div className="grid grid-cols-3 gap-3">
                                        <input className={inputClass} placeholder="Name" value={rentalForm.tenantProfile.emergencyContact.name} onChange={(e) => setRentalForm((f) => ({ ...f, tenantProfile: { ...f.tenantProfile, emergencyContact: { ...f.tenantProfile.emergencyContact, name: e.target.value } } }))} />
                                        <input className={inputClass} placeholder="Phone" value={rentalForm.tenantProfile.emergencyContact.phone} onChange={(e) => setRentalForm((f) => ({ ...f, tenantProfile: { ...f.tenantProfile, emergencyContact: { ...f.tenantProfile.emergencyContact, phone: e.target.value } } }))} />
                                        <input className={inputClass} placeholder="Relationship" value={rentalForm.tenantProfile.emergencyContact.relationship} onChange={(e) => setRentalForm((f) => ({ ...f, tenantProfile: { ...f.tenantProfile, emergencyContact: { ...f.tenantProfile.emergencyContact, relationship: e.target.value } } }))} />
                                    </div>

                                    <p className="text-xs font-medium text-soil-600 pt-1">Previous Landlord Reference</p>
                                    <div className="grid grid-cols-3 gap-3">
                                        <input className={inputClass} placeholder="Name" value={rentalForm.tenantProfile.previousLandlordReference.name} onChange={(e) => setRentalForm((f) => ({ ...f, tenantProfile: { ...f.tenantProfile, previousLandlordReference: { ...f.tenantProfile.previousLandlordReference, name: e.target.value } } }))} />
                                        <input className={inputClass} placeholder="Phone" value={rentalForm.tenantProfile.previousLandlordReference.phone} onChange={(e) => setRentalForm((f) => ({ ...f, tenantProfile: { ...f.tenantProfile, previousLandlordReference: { ...f.tenantProfile.previousLandlordReference, phone: e.target.value } } }))} />
                                        <input className={inputClass} placeholder="Property Address" value={rentalForm.tenantProfile.previousLandlordReference.propertyAddress} onChange={(e) => setRentalForm((f) => ({ ...f, tenantProfile: { ...f.tenantProfile, previousLandlordReference: { ...f.tenantProfile.previousLandlordReference, propertyAddress: e.target.value } } }))} />
                                    </div>
                                </div>
                            ) : (
                                <div className="divide-y divide-soil-100">
                                    <ReadRow icon={Briefcase} label="Occupation" value={profile?.occupation} />
                                    <ReadRow icon={Building2} label="Employer" value={profile?.employer} />
                                    <ReadRow icon={IdCard} label="National ID" value={profile?.nationalId} />
                                    <ReadRow icon={CreditCard} label="KRA PIN" value={profile?.kraPin} />
                                    <ReadRow icon={Users} label="Household Size" value={profile?.tenantProfile?.householdSize} />
                                    <ReadRow icon={Heart} label="Pets" value={profile?.tenantProfile?.pets ? 'Yes' : 'No'} />
                                </div>
                            )}
                        </Card>

                        {/* Preferences & Notifications */}
                        <Card title="Preferences & Notifications">
                            {settingsLoading ? (
                                <div className="flex justify-center py-4"><Loader className="h-5 w-5 text-highland-500 animate-spin" /></div>
                            ) : (
                                <div className="space-y-4">
                                    <div>
                                        <p className="text-xs font-medium text-soil-600 mb-2 flex items-center"><SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" />Search Budget Range (KES)</p>
                                        <div className="grid grid-cols-2 gap-3">
                                            <input type="number" min="0" step="1000" className={inputClass} value={settingsForm.budgetMin} onChange={(e) => setSettingsForm((f) => ({ ...f, budgetMin: Number(e.target.value) }))} placeholder="Min" />
                                            <input type="number" min={settingsForm.budgetMin} step="1000" className={inputClass} value={settingsForm.budgetMax} onChange={(e) => setSettingsForm((f) => ({ ...f, budgetMax: Number(e.target.value) }))} placeholder="Max" />
                                        </div>
                                    </div>

                                    <Field label="Theme">
                                        <select className={inputClass} value={settingsForm.theme} onChange={(e) => setSettingsForm((f) => ({ ...f, theme: e.target.value }))}>
                                            <option value="light">Light</option>
                                            <option value="dark">Dark</option>
                                            <option value="auto">Auto</option>
                                        </select>
                                    </Field>

                                    <div>
                                        <p className="text-xs font-medium text-soil-600 mb-2 flex items-center"><Bell className="h-3.5 w-3.5 mr-1.5" />Notifications</p>
                                        <div className="space-y-2">
                                            <label className="flex items-center space-x-2">
                                                <input type="checkbox" className="h-4 w-4 text-highland-500 border-soil-100 rounded" checked={settingsForm.emailNotifications} onChange={(e) => setSettingsForm((f) => ({ ...f, emailNotifications: e.target.checked }))} />
                                                <span className="text-sm text-soil-700">Email notifications</span>
                                            </label>
                                            <label className="flex items-center space-x-2">
                                                <input type="checkbox" className="h-4 w-4 text-highland-500 border-soil-100 rounded" checked={settingsForm.smsNotifications} onChange={(e) => setSettingsForm((f) => ({ ...f, smsNotifications: e.target.checked }))} />
                                                <span className="text-sm text-soil-700">SMS notifications</span>
                                            </label>
                                            <label className="flex items-center space-x-2">
                                                <input type="checkbox" className="h-4 w-4 text-highland-500 border-soil-100 rounded" checked={settingsForm.pushNotifications} onChange={(e) => setSettingsForm((f) => ({ ...f, pushNotifications: e.target.checked }))} />
                                                <span className="text-sm text-soil-700">Push notifications</span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="flex justify-end pt-1">
                                        <button onClick={handleSaveSettings} disabled={savingSettings} className="flex items-center px-4 py-2 bg-highland-500 text-white rounded-lg text-sm font-medium hover:bg-highland-600 disabled:opacity-50">
                                            {savingSettings ? <Loader className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                                            Save Preferences
                                        </button>
                                    </div>
                                </div>
                            )}
                        </Card>

                        {/* Active Sessions */}
                        <Card title="Active Sessions">
                            {sessionsLoading ? (
                                <div className="flex justify-center py-4"><Loader className="h-5 w-5 text-highland-500 animate-spin" /></div>
                            ) : sessions.length === 0 ? (
                                <p className="text-sm text-soil-400">No active sessions found.</p>
                            ) : (
                                <div className="divide-y divide-soil-100">
                                    {sessions.map((s) => (
                                        <div key={s.sessionId} className="flex items-center justify-between py-2.5">
                                            <div className="flex items-start space-x-3 min-w-0">
                                                <Monitor className="h-4 w-4 text-soil-600 mt-0.5 flex-shrink-0" />
                                                <div className="min-w-0">
                                                    <p className="text-sm text-soil-900 truncate">{s.deviceInfo || 'Unknown device'}</p>
                                                    <p className="text-xs text-soil-400">{s.ipAddress || 'Unknown IP'} · since {new Date(s.createdAt).toLocaleDateString()}</p>
                                                </div>
                                            </div>
                                            <button onClick={() => handleRevokeSession(s.sessionId)} disabled={revokingId === s.sessionId} className="text-xs font-medium text-murram-500 hover:text-murram-600 disabled:opacity-50 flex-shrink-0 ml-3">
                                                {revokingId === s.sessionId ? 'Revoking...' : 'Revoke'}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>

                        {/* Login History */}
                        <Card title="Recent Login Activity">
                            {sessionsLoading ? (
                                <div className="flex justify-center py-4"><Loader className="h-5 w-5 text-highland-500 animate-spin" /></div>
                            ) : loginHistory.length === 0 ? (
                                <p className="text-sm text-soil-400">No login history found.</p>
                            ) : (
                                <div className="divide-y divide-soil-100">
                                    {loginHistory.map((h, i) => (
                                        <div key={i} className="flex items-center justify-between py-2.5">
                                            <div className="flex items-start space-x-3 min-w-0">
                                                <Clock className="h-4 w-4 text-soil-600 mt-0.5 flex-shrink-0" />
                                                <div className="min-w-0">
                                                    <p className="text-sm text-soil-900">
                                                        {h.city ? `${h.city}, ${h.country}` : (h.browser !== 'unknown' ? h.browser : 'Login')}
                                                    </p>
                                                    <p className="text-xs text-soil-400">{new Date(h.timestamp).toLocaleString()}</p>
                                                </div>
                                            </div>
                                            {h.suspicious && (
                                                <span className="text-xs font-medium px-2 py-1 rounded-full bg-murram-100/40 text-murram-600 flex-shrink-0">Suspicious</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserProfile;
